"use strict";
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
var widgets_1 = require("@tableau/widgets");
var React = require("react");
var react_apollo_1 = require("react-apollo");
var Localize_1 = require("../../gen-src/Localize");
var CenteredMessage_1 = require("../components/widgets/CenteredMessage");
var GraphQLErrorFunctions_1 = require("../error/functions/GraphQLErrorFunctions");
var ViewDetailsByPathQueryGql_1 = require("../queries/ViewDetailsByPathQueryGql");
var ItemsSet_1 = require("../utils/ItemsSet");
var ObjectHelpers_1 = require("../utils/ObjectHelpers");
var TypescriptHelpers_1 = require("../utils/TypescriptHelpers");
var LoadingFailedWidget_1 = require("./LoadingFailedWidget");
var ViewerPaneStyles_1 = require("./ViewerPaneStyles");
var VizClientLineagePanelBody_1 = require("./VizClientLineagePanelBody");
var VizClientLineagePanelTitleBar_1 = require("./VizClientLineagePanelTitleBar");
var emptySheetFields = {
    worksheetFields: [],
    datasourceFields: []
};
function getSheetFieldInstances(view) {
    switch (view.__typename) {
        case 'Dashboard': return getSheetFieldInstancesFromDashboard(view);
        case 'Sheet': return getSheetFieldInstancesFromSheet(view);
        default:
            TypescriptHelpers_1.assertUnreachable(view);
            return emptySheetFields;
    }
}
exports.getSheetFieldInstances = getSheetFieldInstances;
function getFieldIdsFromCollection(collection) {
    if (!collection) {
        return [];
    }
    return TypescriptHelpers_1.filterOutNullAndUndefined(collection).map(function (f) { return f.id; });
}
function getSheetFieldInstancesFromSheet(sheet) {
    if (!sheet.sheetFieldInstances) {
        return emptySheetFields;
    }
    var worksheetFields = [];
    var datasourceFields = [];
    var worksheetFieldIds = ItemsSet_1.stringListToKeySet(getFieldIdsFromCollection(sheet.worksheetFields));
    var datasourceFieldIds = ItemsSet_1.stringListToKeySet(getFieldIdsFromCollection(sheet.datasourceFields));
    sheet.sheetFieldInstances.forEach(function (field) {
        if (!field) {
            return;
        }
        if (datasourceFieldIds[field.id]) {
            datasourceFields.push(field);
        }
        else if (worksheetFieldIds[field.id]) {
            worksheetFields.push(field);
        }
    });
    return {
        worksheetFields: worksheetFields,
        datasourceFields: datasourceFields
    };
}
function getSheetFieldInstancesFromDashboard(dashboard) {
    if (!dashboard.upstreamSheetFieldInstances) {
        return emptySheetFields;
    }
    var worksheetFields = [];
    var datasourceFields = [];
    var worksheetFieldIds = {};
    var datasourceFieldIds = {};
    var fieldIdsSeen = {};
    dashboard.sheets.forEach(function (sheet) {
        ItemsSet_1.addStringListToKeySet(worksheetFieldIds, getFieldIdsFromCollection(sheet.worksheetFields));
        ItemsSet_1.addStringListToKeySet(datasourceFieldIds, getFieldIdsFromCollection(sheet.datasourceFields));
    });
    dashboard.upstreamSheetFieldInstances.forEach(function (field) {
        if (!field || fieldIdsSeen[field.id]) {
            return;
        }
        fieldIdsSeen[field.id] = true;
        if (datasourceFieldIds[field.id]) {
            datasourceFields.push(field);
        }
        else if (worksheetFieldIds[field.id]) {
            worksheetFields.push(field);
        }
    });
    return {
        worksheetFields: worksheetFields,
        datasourceFields: datasourceFields
    };
}
function getDatasourcesListFromDatasourceFields(fields) {
    // Get datasources from fields list. Store them in an associative array by ID and
    // collect the values later.
    var datasourceMap = {};
    if (fields) {
        fields.forEach(function (field) {
            if (!field) {
                return;
            }
            // If this is a DatasourceField, it's connecting to a published datasource.
            // Make a note of that.
            var publishedDatasource = null;
            // hasPublishedDatasource is necessary to disambiguate between if a datasource has no
            // published datasource vs if the published datasource is obfuscated
            var hasPublishedDatasource = false;
            if (field.__typename === 'DatasourceField') {
                hasPublishedDatasource = true;
                var remoteField = field.remoteField;
                if (remoteField && remoteField.datasource && remoteField.datasource.__typename === 'PublishedDatasource') {
                    publishedDatasource = remoteField.datasource;
                }
            }
            // Fields without defined data sources do not factor into the list of datasources.
            // If we see the same embedded data source multiple times, but we do not yet have a
            // published data source, parse the field again to try finding a published data source.
            if (field.datasource && !datasourceMapsToPublishedDatasource(datasourceMap, field.datasource.id)) {
                datasourceMap[field.datasource.id] =
                    datasourceToVizClientDatasource(field.datasource, publishedDatasource, hasPublishedDatasource, field.upstreamDatabasesConnection);
            }
        });
    }
    // True if the a DatasourceMap contains a given key and the key maps to a value with a
    // published data source.
    function datasourceMapsToPublishedDatasource(map, id) {
        if (!map[id]) {
            return false; // map does not contain the given key
        }
        return map[id].hasPublishedDatasource;
    }
    // Hard-coding the language because sort behavior in Tableau isn't typically expected
    // to change based on the browser locale. This isn't ideal and should be revisited if
    // it is found to cause customer pain.
    return ObjectHelpers_1.objectValues(datasourceMap).sort(function (a, b) {
        return (a.name || '').localeCompare((b.name || ''), 'en', { sensitivity: 'base' });
    });
}
exports.getDatasourcesListFromDatasourceFields = getDatasourcesListFromDatasourceFields;
// Convert a returnedDatasource into a datasource suitable for use in the component.
function datasourceToVizClientDatasource(datasource, publishedDatasource, hasPublishedDatasource, upstreamDatabasesConnection) {
    return {
        id: datasource.id,
        name: datasource.name,
        __typename: datasource.__typename,
        hasExtracts: datasource.hasExtracts,
        extractLastUpdateTime: datasource.extractLastUpdateTime,
        hasPublishedDatasource: hasPublishedDatasource,
        publishedDatasource: publishedDatasource,
        databasesConnectionNodes: upstreamDatabasesConnection ?
            upstreamDatabasesConnection.nodes : null
    };
}
var ViewDetailsByPathWrapper = /** @class */ (function (_super) {
    __extends(ViewDetailsByPathWrapper, _super);
    function ViewDetailsByPathWrapper() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.titleBar = function () {
            return (React.createElement(VizClientLineagePanelTitleBar_1.VizClientLineagePaneTitleBar, { title: Localize_1.Messages.DataDetailsPaneTitle(), onCloseRequested: _this.props.onCloseRequested }));
        };
        _this.renderViewDetails = function (data) {
            // Querying a sheet by its own path should return exactly 1 sheet, unless the graph is not fully populated.
            // All sheets are expected to have a workbook.
            if (!data
                || !data.views
                || data.views.length === 0
                || !data.views[0]
                || !data.views[0].workbook) {
                return _this.renderIngestionInProgress();
            }
            var responseView = data.views[0];
            var responseWorkbook = data.views[0].workbook;
            var responseOwner = responseWorkbook.owner;
            var _a = getSheetFieldInstances(responseView), datasourceFields = _a.datasourceFields, worksheetFields = _a.worksheetFields;
            var datasources = getDatasourcesListFromDatasourceFields(datasourceFields);
            return (React.createElement(ViewerPaneStyles_1.LineagePanelContainer, { "data-tb-test-id": _this.props.testId },
                _this.titleBar(),
                React.createElement(VizClientLineagePanelBody_1.VizClientLineagePanelBody, { viewName: responseView.name, viewType: responseView.__typename, workbookName: responseWorkbook.name, workbookVizportalUrlId: responseWorkbook.vizportalUrlId, projectName: responseWorkbook.projectName, projectVizportalUrlId: responseWorkbook.projectVizportalUrlId, owner: responseOwner, datasources: datasources, datasourceFields: datasourceFields, worksheetFields: worksheetFields, modifiedDate: responseView.updatedAt, vizPortalWebClientRequester: _this.props.vizPortalWebClientRequester, viewPath: _this.props.viewPath, showSparkline: _this.props.showSparkline })));
        };
        _this.loadingIndicator = function () {
            return (React.createElement(ViewerPaneStyles_1.LineagePanelContainer, { "data-tb-test-id": _this.props.testId },
                React.createElement("div", { className: ViewerPaneStyles_1.loadingIndicator, "aria-busy": 'true' },
                    React.createElement(widgets_1.ActivitySpinnerWidget, { testId: 'lineage-loading-details' }),
                    React.createElement("div", { className: 'lineage-loading-details-message' }, Localize_1.Messages.LoadingDataDetails()))));
        };
        _this.renderError = function (error, refetch) {
            // If we received an empty list of errors, display a generic error.
            if (!error.graphQLErrors || error.graphQLErrors.length === 0) {
                return _this.renderPaneWithError(Localize_1.Messages.DataDetailsGenericErrorDetails());
            }
            return (React.createElement(ViewerPaneStyles_1.LineagePanelContainer, { "data-tb-test-id": _this.props.testId },
                _this.titleBar(),
                React.createElement(LoadingFailedWidget_1.LoadingFailedWidget, { key: 'loadingFailedWidget', errorCode: GraphQLErrorFunctions_1.getFirstGraphQLErrorCode(error), onRetryRequested: function () { return refetch({ viewPath: _this.props.viewPath }); } })));
        };
        _this.renderIngestionInProgress = function () {
            return _this.renderPaneWithError(Localize_1.Messages.ErrorItemNotFound());
        };
        // Display a pane with a single error centered.
        _this.renderPaneWithError = function (errorMessage) {
            return (React.createElement(ViewerPaneStyles_1.LineagePanelContainer, { "data-tb-test-id": _this.props.testId },
                React.createElement(VizClientLineagePanelTitleBar_1.VizClientLineagePaneTitleBar, { title: Localize_1.Messages.Error(), onCloseRequested: _this.props.onCloseRequested }),
                React.createElement("div", { className: ViewerPaneStyles_1.lineagePanelBodyWithError },
                    React.createElement(CenteredMessage_1.CenteredMessage, { message: errorMessage }))));
        };
        return _this;
    }
    ViewDetailsByPathWrapper.prototype.render = function () {
        var _this = this;
        return (React.createElement(react_apollo_1.Query, { query: ViewDetailsByPathQueryGql_1.ViewDetailsByPathQueryGql, variables: { viewPath: this.props.viewPath } }, function (_a) {
            var loading = _a.loading, error = _a.error, data = _a.data, refetch = _a.refetch;
            if (loading) {
                return _this.loadingIndicator();
            }
            if (error) {
                return _this.renderError(error, refetch);
            }
            return _this.renderViewDetails(data);
        }));
    };
    return ViewDetailsByPathWrapper;
}(React.Component));
exports.ViewDetailsByPathWrapper = ViewDetailsByPathWrapper;
//# sourceMappingURL=ViewDetailsByPathWrapper.js.map